import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Particle from "../../Components/Particle.js";
import DynamicForm from "../../Components/DynamicForm";
import ImageCropper from "../../Components/ImageCropper";
import { END_POINT_MEMBERS, END_POINT_UPLOAD, API_BASE } from "../../Config/index.js";
import { registrationForm } from "../../Constants";
import { useAuth } from "../../Context/AuthContext";

function StepIndicator({ total, current }) {
  return (
    <div className="flex items-center justify-center gap-2 py-4">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-2 rounded-full transition-all duration-300 ${
            i < current ? 'bg-blue-500 w-6' : i === current ? 'bg-blue-400 w-8' : 'bg-gray-600 w-4'
          }`}
        />
      ))}
    </div>
  );
}

function Onboarding() {
  const TOTAL_STEPS = registrationForm.length + 1; // +1 for photo step
  const [step, setStep] = useState(0); // 0 = photo step
  const [formData, setFormData] = useState({});
  const [profileFile, setProfileFile] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const { user, token, updateUser } = useAuth();
  const navigate = useNavigate();

  const isPhotoStep = step === 0;
  const formStepIndex = step - 1;
  const currentFormStep = isPhotoStep ? null : registrationForm[formStepIndex];
  const isLastStep = step === TOTAL_STEPS - 1;

  function handlePhotoNext() {
    setStep(1);
  }

  function handleNext() {
    if (step < TOTAL_STEPS - 1) {
      setStep(s => s + 1);
    }
  }

  function handlePrevious() {
    if (step > 0) setStep(s => s - 1);
  }

  function onChange(data) {
    setFormData(prev => ({ ...prev, ...data }));
  }

  function handleImageDone(file, url) {
    setProfileFile(file);
    setProfilePreview(url);
  }

  async function handleSubmit() {
    setLoading(true);
    setApiError('');
    try {
      let profileImageData = null;
      if (profileFile) {
        const fd = new FormData();
        fd.append('file', profileFile);
        const uploadRes = await axios.post(END_POINT_UPLOAD, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        profileImageData = uploadRes.data?.data;
      }

      const payload = {
        ...formData,
        onboardingComplete: true,
        ...(profileImageData ? { profileImage: profileImageData } : {})
      };

      await axios.put(`${END_POINT_MEMBERS}/${user?.userId || user?._id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      updateUser({ ...user, onboardingComplete: true });
      navigate('/members');
    } catch (err) {
      setApiError(err?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <section>
      <div className="home-about-section w-full" id="home">
        <Particle />
        <div className="home-content">
          <div className="flex justify-center px-4">
            <div className="w-full max-w-lg">
              <div className="bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">

                <div className="px-6 pt-6">
                  <h2 className="text-xl font-bold text-white text-center mb-0">
                    Complete your profile
                  </h2>
                  <StepIndicator total={TOTAL_STEPS} current={step} />
                </div>

                {apiError && (
                  <div className="mx-6 mb-2 px-4 py-3 bg-red-900/40 border border-red-600 rounded-xl text-red-300 text-sm">
                    {apiError}
                  </div>
                )}

                <div className="px-6 pb-6">
                  {isPhotoStep ? (
                    <div>
                      <p className="text-gray-300 text-sm text-center mb-4">
                        Add a profile photo so members can recognise you
                      </p>
                      <ImageCropper
                        onDone={handleImageDone}
                        currentImage={profilePreview}
                      />
                      <div className="flex gap-3 mt-6">
                        <button
                          type="button"
                          onClick={handlePhotoNext}
                          className="flex-1 py-3 rounded-xl bg-gray-700 text-gray-300 font-medium"
                        >
                          Skip
                        </button>
                        <button
                          type="button"
                          onClick={handlePhotoNext}
                          disabled={!profilePreview}
                          className="flex-1 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-40"
                        >
                          {profilePreview ? 'Next' : 'Skip for now'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-white font-semibold mb-4 text-lg">
                        {currentFormStep.label}
                      </h3>
                      <form>
                        {currentFormStep.config.map((item) => (
                          <DynamicForm
                            key={item.fieldName}
                            formConfig={{ ...item, onChange, formName: currentFormStep.formName }}
                            isShowUpload={true}
                            setShowUpload={() => {}}
                            formData={formData}
                          />
                        ))}
                        <div className="flex gap-3 mt-6">
                          <button
                            type="button"
                            onClick={handlePrevious}
                            className="flex-1 py-3 rounded-xl bg-gray-700 text-gray-300 font-medium"
                          >
                            Back
                          </button>
                          {isLastStep ? (
                            <button
                              type="button"
                              onClick={handleSubmit}
                              disabled={loading}
                              className="flex-1 py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
                            >
                              {loading ? 'Saving…' : 'Finish & Go'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={handleNext}
                              className="flex-1 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                            >
                              Next
                            </button>
                          )}
                        </div>
                      </form>
                    </div>
                  )}
                </div>

              </div>
              <p className="text-gray-500 text-xs text-center mt-4">
                Step {step + 1} of {TOTAL_STEPS}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Onboarding;
